import { useState } from "react";
import { HelpCircle } from "lucide-react";
import { Button } from "@/ui/button";
import { NAV_GROUPS } from "@/components/sidebar/nav-items";
import { GUIDE_BOOK } from "./content";
import { resolveGuideId } from "./resolve-guide-id";
import { GuideDialog } from "./GuideDialog";
import { SalaryGuideDialog } from "./SalaryGuideDialog";
import { MultiTabGuideDialog } from "./MultiTabGuideDialog";
import { SALARY_GUIDE_ID } from "./salary-guide-tabs";
import { MULTI_TAB_GUIDES } from "./multi-tab-guides";
import { usePageGuideLocation } from "./location-context";

// Nhãn trang lấy từ menu để hộp thoại gọi đúng tên người dùng đang thấy.
const LABELS: Record<string, string> = (() => {
  const m: Record<string, string> = {};
  for (const g of NAV_GROUPS) for (const it of g.items) m[it.id] = it.label;
  return m;
})();

/**
 * Nút "Hướng dẫn" đầu trang.
 *
 * Tự suy ra trang đang mở từ đường dẫn nên mọi màn có `PageHeader` đều có nút,
 * không phải đi sửa từng trang. Trang chưa có bài hướng dẫn thì nút không hiện
 * — thà vắng nút còn hơn mở ra một hộp rỗng.
 */
export function GuideButton({
  guideId,
  guideTab,
  compact = false,
}: {
  guideId?: string | undefined;
  guideTab?: string | undefined;
  /**
   * Chỉ biểu tượng, không chữ — cho đầu cột chật (cột khách 340px của màn Chat
   * khách, nơi thanh đầu trang đã ẩn). Tên nút vẫn đọc được qua trình đọc màn
   * hình và khi rê chuột.
   */
  compact?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const loc = usePageGuideLocation();
  // Ngoài khung ứng dụng (bài kiểm component dựng PageHeader trần) thì không
  // có đường dẫn để suy ra trang — nút tự ẩn thay vì nổ.
  const id = guideId ?? (loc ? resolveGuideId(loc.pathname, loc.tab) : null);
  const guide = id ? GUIDE_BOOK[id] : undefined;
  if (!id || !guide) return null;

  // Ba loại hộp, chọn bằng BẢNG TRA chứ không nối thêm nhánh:
  //   · trang có nhiều thẻ, mỗi thẻ một việc rời nhau → hộp nhiều thẻ. Trang
  //     nào thuộc loại này khai trong `MULTI_TAB_GUIDES`, nên thêm trang mới
  //     là thêm một dòng dữ liệu, không phải sửa chỗ này.
  //   · trang Cơ chế lương → hộp riêng, vì bài DÀI chứ không vì nhiều thẻ; thẻ
  //     của nó là thẻ của BÀI (dòng tiền, cách chạy…), không phải thẻ của màn.
  //   · còn lại → hộp chung.
  const Hop = MULTI_TAB_GUIDES[id]
    ? MultiTabGuideDialog
    : id === SALARY_GUIDE_ID
      ? SalaryGuideDialog
      : GuideDialog;

  return (
    <>
      {compact ? (
        <Button
          type="button"
          variant="ghost"
          size="icon"
          onClick={() => setOpen(true)}
          className="text-muted"
          title="Hướng dẫn sử dụng trang này"
          aria-label="Hướng dẫn sử dụng trang này"
        >
          <HelpCircle aria-hidden />
        </Button>
      ) : (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => setOpen(true)}
          className="min-w-[var(--touch-min)] text-muted sm:min-w-0"
          title="Hướng dẫn sử dụng trang này"
        >
          <HelpCircle aria-hidden />
          <span className="hidden sm:inline">Hướng dẫn</span>
          <span className="sr-only sm:hidden">Hướng dẫn sử dụng trang này</span>
        </Button>
      )}
      <Hop
        open={open}
        onOpenChange={setOpen}
        title={LABELS[id] ?? "trang này"}
        guide={guide}
        guideTab={guideTab}
        guideId={id}
      />
    </>
  );
}
